/**
 * Error Logging Utility for New Relic Integration
 *
 * This utility provides a simple interface for logging errors to New Relic
 * with automatic user context and custom attributes.
 */

import { flattenObject, getUtm } from "./index";
import { getClientInfo, getUrlComponents } from "./clientInfoUtils";

type ERROR_CONTEXT =
  | "REACT_ERROR_BOUNDARY"
  | "REDUX_MIDDLEWARE_ERROR"
  | "API_ERROR"
  | "VALIDATION_ERROR"
  | "GLOBAL_UNHANDLED_ERROR"
  | "UNHANDLED_PROMISE_REJECTION"
  | "CAUGHT_PROMISE_REJECTION"
  | "CAUGHT_ERROR"
  | "ACTION_ERROR"
  | "NETWORK_ERROR";

export interface Newrelic {
  log: (eventName: string, options: Record<string, unknown>) => void;
  noticeError?: (
    error: Error | string,
    customAttributes?: Record<string, unknown>
  ) => void;
  setCustomAttribute?: (name: string, value: string | number | boolean) => void;
  setUserId?: (userId: string) => void;
  addPageAction?: (
    actionName: string,
    customAttributes?: Record<string, unknown>
  ) => void;
}

declare const window: Window & { newrelic: Newrelic };

export interface LogErrorOptions {
  /** Custom message to provide context about when/where the error occurred */
  message?: string;
  context?: ERROR_CONTEXT;
  /** Additional custom attributes to include with the error */
  customAttributes?: Record<string, unknown>;
  /** Whether to log to console as well (default: true in development) */
  logToConsole?: boolean;
}

export function getErrorLogger({
  context,
  message,
  customAttributes,
}: {
  context?: ERROR_CONTEXT;
  message?: string;
  customAttributes?: Record<string, unknown>;
} = {}) {
  return (error: Error | string, options: LogErrorOptions = {}) => {
    const mergedCustomAttributes = {
      ...customAttributes,
      ...options.customAttributes,
    };

    const { customAttributes: _, ...restOptions } = options;
    logError(error, {
      context,
      message,
      customAttributes: mergedCustomAttributes,
      ...restOptions,
    });
  };
}

export function logError(
  error: Error | string,
  options: LogErrorOptions = {}
): void {
  const {
    message,
    context,
    customAttributes,
    logToConsole = process.env.NODE_ENV === "development",
  } = options;

  // Log to console in development or if explicitly requested
  if (logToConsole) {
    // eslint-disable-next-line no-console
    console.error("Error:", {
      error,
      context,
      message,
      customAttributes,
    });
  }

  if (!window.newrelic?.noticeError) {
    // eslint-disable-next-line no-console
    console.warn("New Relic recordError method not available");
    return;
  }

  const { url } = getUrlComponents();
  // Build custom attributes from options
  const finalCustomAttributes: Record<string, unknown> = {
    __context: context,
    __message: message,
    __env: process.env.REACT_APP_ENVIRONMENT || "development",
    __timestamp: new Date().toISOString(),
    __url: url,
    ...flattenObject(customAttributes || {}, "__"),
    //...flattenObject(userDetails || {}, '__user'),
    ...flattenObject(getClientInfo(), "__client"),
    //userId: userDetails?.userId,
    ...flattenObject(getUtm(), "__utm"),
  };

  // Log to New Relic
  window.newrelic.noticeError(error, finalCustomAttributes);
}

/**
 * Logs an API error with request/response context
 *
 * @param error - The error that occurred
 * @param endpoint - The API endpoint that failed
 * @param method - HTTP method used
 * @param statusCode - HTTP status code received (if available)
 * @param customAttributes - Additional attributes to include
 *
 * @example
 * ```typescript
 * try {
 *   const response = await fetch('/api/users', { method: 'POST' });
 *   if (!response.ok) {
 *     logApiError(
 *       new Error('API request failed'),
 *       '/api/users',
 *       'POST',
 *       response.status,
 *       { requestBody: userData }
 *     );
 *   }
 * } catch (error) {
 *   logApiError(error, '/api/users', 'POST');
 * }
 * ```
 */
export function logApiError(
  error: Error | string,
  endpoint: string,
  method = "GET",
  statusCode?: number,
  customAttributes?: Record<string, unknown>,
  isNetworkError?: boolean
): void {
  logError(error, {
    message: `API request failed: ${method} ${endpoint}`,
    context: isNetworkError ? "NETWORK_ERROR" : "API_ERROR",
    customAttributes: {
      ...customAttributes,
      endpoint,
      method,
      statusCode,
    },
  });
}

/**
 * Logs a validation error with field context
 *
 * @param error - The validation error
 * @param field - The field that failed validation
 * @param value - The invalid value (will be stringified, avoid logging sensitive data)
 * @param customAttributes - Additional attributes to include
 */
export function logValidationError(
  error: Error | string,
  field: string,
  value?: unknown,
  customAttributes?: Record<string, unknown>
): void {
  logError(error, {
    message: `Validation failed for field: ${field}`,
    context: "VALIDATION_ERROR",
    customAttributes: {
      ...customAttributes,
      errorType: "validation_error",
      field,
      invalidValue: value ? String(value).substring(0, 100) : undefined, // Limit length for safety
    },
  });
}

/**
 * Sets up global error handlers for unhandled errors and promise rejections
 * This is typically called once during app initialization
 */
export function setupGlobalErrorHandlers(): void {
  // Handle unhandled errors
  window.addEventListener("error", (event) => {
    logError(event.error || event.message, {
      message: "Unhandled error occurred",
      context: "GLOBAL_UNHANDLED_ERROR",
      customAttributes: {
        errorType: "unhandled_error",
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  // Handle unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    logError(event.reason || "Unhandled promise rejection", {
      message: "Unhandled promise rejection",
      context: "UNHANDLED_PROMISE_REJECTION",
      customAttributes: {
        errorType: "unhandled_promise_rejection",
      },
    });
  });
}

// Export as default for convenience
export default logError;
